import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { HealthRing, Empty } from "../components/ui";
import { buildGraphFromWorkspace } from "../lib/graphFromWorkspace";
import { orgById, childOrgs, rollupOrg } from "../lib/graph";
import { LayoutGrid } from "../components/widgets";
import { LayoutEditor } from "../components/LayoutEditor";
import { PlanRuns } from "../components/PlanRuns";
import * as work from "../lib/workClient";
import type { Layout, Playbook, PlaybookRun, Account } from "../lib/types";

type Tab = "overview" | "plan";

export default function Relationship360() {
  const { id } = useParams<{ id: string }>();
  const { ws } = useWorkspace();
  const g = useMemo(() => buildGraphFromWorkspace(ws), [ws]);
  const org = id ? orgById(g, id) : undefined;

  const [mode, setMode] = useState<work.Mode>("local");
  const [layout, setLayout] = useState<Layout | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<Tab>("overview");
  const [runs, setRuns] = useState<PlaybookRun[]>([]);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [applying, setApplying] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const members = org ? (childOrgs(g, org.id).length ? childOrgs(g, org.id) : [org]) : [];
  const memberAccounts = members.map((m) => ws.accounts.find((a) => a.id === (m.accountId ?? m.id))).filter((a): a is Account => !!a);
  const anchor = memberAccounts[0];
  const roll = org ? rollupOrg(g, org.id) : null;

  const refresh = async () => {
    const r = await work.loadPlaybookRuns(ws); setMode(r.mode);
    setRuns(r.runs.filter((run) => run.scopeKind === "relationship" && run.scopeId === id));
    setPlaybooks((await work.loadPlaybooks(ws)).playbooks);
  };
  useEffect(() => {
    work.loadLayouts(ws).then((r) => { setMode(r.mode); setLayout(r.layouts.find((l) => l.entity === "relationship") ?? null); });
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [ws, id]);
  const say = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2800); };

  if (!org) return <div className="page"><Empty><p style={{ marginBottom: 12 }}>That relationship could not be found.</p><Link to="/relationships" className="btn btn--ghost btn--sm"><Icon name="arrowRight" size={14} /> Back to relationships</Link></Empty></div>;

  const save = async (sections: Layout["sections"]) => {
    if (!layout) return;
    setLayout({ ...layout, sections }); setEditing(false);
    await work.saveLayout(mode, ws, layout.id, sections);
  };
  const ctxAccount = anchor ?? ws.accounts[0];

  return (
    <div className="page">
      {editing && layout && <LayoutEditor layout={layout} entity="relationship" onSave={save} onClose={() => setEditing(false)} />}

      <p className="eyebrow" style={{ marginBottom: 14 }}>
        <Link to="/relationships" className="faint">Relationships</Link> · {org.type}
      </p>

      <div className="detail-hd">
        <span className="mono mono--lg" style={{ background: org.color, color: "#fff" }}>{org.name[0]}</span>
        <div className="meta">
          <h1>{org.name}</h1>
          <div className="subline">
            <span>{org.type}</span>
            <span>{memberAccounts.length} account{memberAccounts.length === 1 ? "" : "s"}</span>
            {roll && <span>{roll.instances} deployments</span>}
            {roll && <span>{roll.activeUsers} active users</span>}
          </div>
        </div>
        <div className="row" style={{ gap: 20, alignItems: "center" }}>
          {roll && <HealthRing value={roll.health} size={72} stroke={6} />}
          <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}><Icon name="grid" size={14} /> Customize</button>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 18 }}>
        {(["overview", "plan"] as Tab[]).map((t) => (
          <button key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t === "overview" ? "Overview" : "Plan"}</button>
        ))}
      </div>

      <div style={{ marginTop: 20 }}>
        {tab === "overview" && (layout ? <LayoutGrid sections={layout.sections} ctx={{ entity: "relationship", ws, account: ctxAccount, relationship: { orgId: org.id, name: org.name, type: org.type } }} /> : <Empty>Loading layout…</Empty>)}

        {tab === "plan" && (
          <div style={{ display: "grid", gap: 16 }}>
            {flash && <div className="badge badge--good">{flash}</div>}
            <div className="row between">
              <div className="faint" style={{ fontSize: 13 }}>{runs.length} relationship plan{runs.length === 1 ? "" : "s"} · spans {memberAccounts.length} account{memberAccounts.length === 1 ? "" : "s"}</div>
              <button className="btn btn--primary btn--sm" disabled={!anchor} onClick={() => setApplying(true)}><Icon name="route" size={14} /> Apply a playbook</button>
            </div>
            <PlanRuns runs={runs} mode={mode} onChanged={refresh} />
          </div>
        )}
      </div>

      {applying && anchor && <ApplyToRelationship org={{ id: org.id, name: org.name }} anchorName={anchor.name} playbooks={playbooks}
        onClose={() => setApplying(false)}
        onApply={async (playbookId, start) => {
          const r = await work.launchPlaybook(mode, ws, playbookId, { accountId: anchor.id, scopeKind: "relationship", scopeId: org.id, scopeName: org.name, planStartDate: start });
          setApplying(false); if (r) { say("Relationship plan applied — tasks scheduled"); setTab("plan"); refresh(); }
        }} />}
    </div>
  );
}

function ApplyToRelationship({ org, anchorName, playbooks, onClose, onApply }: {
  org: { id: string; name: string }; anchorName: string; playbooks: Playbook[];
  onClose: () => void; onApply: (playbookId: string, start: string) => void;
}) {
  const eligible = playbooks.filter((p) => p.scope === "relationship" || p.scope === "both");
  const list = eligible.length ? eligible : playbooks;
  const [playbookId, setPlaybookId] = useState(list.find((p) => p.id === "p_onboard")?.id ?? list[0]?.id ?? "");
  const [start, setStart] = useState("2026-09-01");
  const pb = playbooks.find((p) => p.id === playbookId);
  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="compose-modal__hd"><div className="row" style={{ gap: 8 }}><Icon name="route" size={16} /><b>Apply to {org.name}</b></div><button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button></div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14 }}>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Playbook</span>
            <select className="inp" value={playbookId} onChange={(e) => setPlaybookId(e.target.value)}>{list.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.steps.length} steps)</option>)}</select>
          </label>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Start date</span>
            <input className="inp" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          {pb && <div className="faint" style={{ fontSize: 12 }}>Scheduled at the relationship level; tasks land on {anchorName} (the anchor account) and roll up here.</div>}
        </div>
        <div className="compose-modal__hd" style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "none", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!playbookId} onClick={() => onApply(playbookId, start)}>Apply plan</button>
        </div>
      </div>
    </div>
  );
}
